
// https://identity.app.masterdocs.com.au/auth/js/keycloak.js
import Keycloak from "keycloak-js";
// import Bottleneck from "bottleneck";
import { once, takeLast } from "ramda";
import { getMDShortCutToken } from "./login.api";
import { END, buffers, eventChannel } from "redux-saga";
import {
    all, call, cancelled, delay, put, race, take, takeEvery, takeLatest, throttle
} from "redux-saga/effects";
import { choosekeycloakhost, getNewToken } from "./loginUtil";

import {
    LOGIN_REQUESTED,
    LOGIN_SUCCEEDED,
    LOGOUT_REQUESTED,
    LOGOUT_SUCCEEDED,
    ACCEPT_INVITE,
    loginFailed,
    loginRequested,
    loginSucceeded,
    logoutRequested,
    logoutSucceeded
} from "./login.duck";
import { acceptInviteUser } from "../auth/auth.duck";
const isDevelopment = process.env.NODE_ENV === "development";

const getConfig = async function () {
    const [
        host, ssl, realm, clientId
    ] = await choosekeycloakhost();
    return {
        url: `http${ssl ? "s" : ""}://${host}/auth`,
        realm,
        clientId,
        onLoad: "check-sso"
    };
};
let latestJti = null;
export const init = once(async () => {
    const config = await getConfig();
    const keycloak = new Keycloak({
        url: config.url,
        realm: config.realm,
        clientId: config.clientId,
        onLoad: config.onLoad,
        silentCheckSsoRedirectUri: `${window.location.origin}/silent-check-sso.html`,
        checkLoginIframe: true,
        checkLoginIframeInterval: 1000 * 3,
    });
    const isUpdating = Symbol("TOKEN_UPDATING");
    const mdToken = Symbol("MD_TOKEN");
    const rmdToken = Symbol("RESOLVED_MD_TOKEN");
    const keycloakProxy = {
        ...Object.fromEntries([...Object.getOwnPropertyNames(keycloak).map(p => [ p, keycloak[p] ]),]),
        isTokenExpired: n => {
            if (!keycloak.isTokenExpired(n)) {
                return false;
            }
            return keycloakProxy[isUpdating];
        },
        updateToken: async ts => {
            let timeLocal = new Date().getTime();
            const { tokenParsed, token, idTokenParsed, idToken } = await getNewToken(keycloak, ts);
            timeLocal = (timeLocal + new Date().getTime()) / 2;
            let isTokenChanged = false;
            if (tokenParsed.jti !== latestJti) {
                isTokenChanged = true;
                keycloak.tokenParsed = tokenParsed;
                keycloak.token = token;
                keycloak.idTokenParsed = idTokenParsed;
                keycloak.idToken = idToken;
                keycloak.sessionId = keycloak.tokenParsed.session_state;
                keycloak.authenticated = true;
                keycloak.subject = keycloak.tokenParsed.sub;
                keycloak.realmAccess = keycloak.tokenParsed.realm_access;
                keycloak.resourceAccess = keycloak.tokenParsed.resource_access;
                keycloak.timeSkew = Math.floor(timeLocal / 1000) - keycloak.tokenParsed.iat;
                latestJti = tokenParsed.jti;
                if (keycloak.tokenTimeoutHandle) {
                    clearTimeout(keycloak.tokenTimeoutHandle);
                    keycloak.tokenTimeoutHandle = null;
                    if(keycloak.onTokenExpired) {
                        const expiresIn = (keycloak.tokenParsed.exp - (new Date().getTime() / 1000) + keycloak.timeSkew) * 1000;
                        if (expiresIn <= 0) {
                            keycloak.onTokenExpired();
                        } else {
                            keycloak.tokenTimeoutHandle = setTimeout(keycloak.onTokenExpired, expiresIn);
                        }
                    }
                }
            }
            keycloakProxy[isUpdating] = true;
            keycloakProxy[mdToken] = getMDShortCutToken(keycloak.token);
            keycloakProxy[rmdToken] = await keycloakProxy[mdToken];
            if(isTokenChanged){
                keycloak.onAuthRefreshSuccess && keycloak.onAuthRefreshSuccess();
            }
            return { tokenParsed, token, idTokenParsed, idToken };
        },
        login: async (...args) => {
            const loginresult = await keycloak.login(...args);
            keycloakProxy[isUpdating] = true;
            keycloakProxy[mdToken] = getMDShortCutToken(keycloak.token);
            keycloakProxy[rmdToken] = await keycloakProxy[mdToken];
            return loginresult;
        },
        get token() {
            return keycloakProxy[rmdToken] || keycloak.token;
        },
        getMDToken() {
            return keycloakProxy[mdToken] || getMDShortCutToken(keycloak.token);
        },
        getKeycloak() {
            return keycloak;
        }
    };
    // { authenticated, token, tokenParsed, subject, idToken, idTokenParsed, realmAccess, resourceAccess, refreshToken, refreshTokenParsed } = keycloak
    keycloak.onAuthSuccess = (...args) => keycloakProxy.onAuthSuccess && keycloakProxy.onAuthSuccess(...args);
    keycloak.onAuthError = (...args) => keycloakProxy.onAuthError && keycloakProxy.onAuthError(...args);
    keycloak.onAuthRefreshSuccess = (...args) => keycloakProxy.onAuthRefreshSuccess && keycloakProxy.onAuthRefreshSuccess(...args);
    keycloak.onAuthRefreshError = (...args) => keycloakProxy.onAuthRefreshError && keycloakProxy.onAuthRefreshError(...args);
    keycloak.onAuthLogout = (...args) => keycloakProxy.onAuthLogout && keycloakProxy.onAuthLogout(...args);
    keycloak.onTokenExpired = (...args) => keycloakProxy.onTokenExpired && keycloakProxy.onTokenExpired(...args);
    return keycloakProxy;
});

const initOnce = once(kc => kc.init({
    flow: "implicit", // todo: change me back to "hybrid"
    useNonce: true,
    silentCheckSsoRedirectUri: `${window.location.origin}/silent-check-sso.html`,
    silentCheckSsoFallback: true,
    token: localStorage.getItem("hybridToken") || undefined,
    refreshToken: undefined,
    idToken: undefined,
    enableLogging: isDevelopment,
    messageReceiveTimeout: 10000,
}));

const loginStateChange = kc => {
    return eventChannel(emitter => {
        kc.onAuthSuccess = detail => {
            emitter({ state: "LOGGEDIN", detail });
        };
        kc.onAuthError = detail => emitter({ state: "ERROR", detail });
        kc.onAuthRefreshSuccess = async detail => {
            emitter({ state: "LOGGEDIN", detail });
        };
        kc.onAuthRefreshError = detail => emitter({ state: "ERROR", detail });
        kc.onAuthLogout = detail => emitter({ state: "LOGGEDOUT", detail });
        kc.onTokenExpired = detail => emitter({ state: "LOGGEDOUT", detail });
        initOnce(kc).then(loggedIn => {
            emitter({ state: loggedIn ? "LOGGEDIN" : "LOGGEDOUT", detail: null });
        });

        return () => { // unset all the event listener
            delete kc.onAuthSuccess;
            delete kc.onAuthError;
            delete kc.onAuthRefreshSuccess;
            delete kc.onAuthRefreshError;
            delete kc.onAuthLogout;
            delete kc.onTokenExpired;
        };
    },
    buffers.sliding(1) // buffer one invocation holding the most recent event
    );
};

function* refreshSession() {
    while (true) {
        yield take(LOGIN_SUCCEEDED);
        const kc = yield call(init);
        while (true) {
            const { logout } = yield race({
                logout: take(LOGOUT_SUCCEEDED),
                updateToken: delay(1000)
            });
            if (logout) { break; }
            const { logout: logout2 } = yield race({
                logout: take(LOGOUT_SUCCEEDED),
                updated: call(async t => {
                    try {
                        const result = await kc.updateToken(t);
                        return result || {};
                    } catch(e) {
                        throw e || new Error("An unknown error occured - Check refreshSession");
                    }
                }, 30)
            });
            if (logout2) { break; }
        }
    }
}

const actions = {
    LOGGEDIN: (data, kc) => loginSucceeded({ ...data, token: kc.token }),
    ERROR: loginFailed,
    LOGGEDOUT: data => logoutSucceeded({ ...data, token: null }),
};

function* loginStateSaga() {
    const kc = yield call(init);
    const chan = yield call(loginStateChange, kc);
    let state = "LOADING"; // LOADING, LOGGEDIN, LOGGEDOUT, ERROR
    let message = null;
    try {
        while (true) {
            const { login, logout, statechange } = yield race({
                login: take(LOGIN_REQUESTED),
                logout: take(LOGOUT_REQUESTED),
                statechange: take(chan),
            });
            if (statechange?.state === "ERROR") {
                kc.clearToken();
                throw new Error(statechange.detail);
            }
            if (statechange) {
                state = statechange.state;
                message = statechange.detail || {};
                yield put(actions[state](message, kc));
                continue;
            }
            if (login && state !== "LOGGEDIN") {
                initOnce(kc).then(() => kc.login({
                    /* redirectUri, prompt, maxAge, loginHint, scope: "openid mdv3api", idpHint, action: "login" || "register", locale: "" */
                    ...login,
                    scope: "mdv3api openid email profile"
                }));
                state = "LOADING";
            }
            if (logout && state !== "LOGGEDOUT") {
                initOnce(kc).then(() => kc.logout());
                state = "LOADING";
            }
            // other: kc.isTokenExpired, kc.updateToken, kc.clearToken
        }
    } finally {
        if (yield cancelled()) {
            chan.close();
        }
    }

}

function* persistToken() {
    const kc = yield call(init);
    localStorage.setItem("hybridToken", kc.getKeycloak().token);
}

function* fnInviteUser(){
    yield put(acceptInviteUser());
}

function* cleanupToken() {
    localStorage.removeItem("hybridToken");
}

function* redirectToLogIn() {
    yield put(loginRequested({}));
}

function* refreshSessionOrLogIn({ data: { token } }) {
    const kc = yield call(init);
    const kcToken = kc.getKeycloak().token;

    if (document.querySelector("#load-mask")?.style?.display === "none") {
        window?.app?.Duchess.refreshSessionv3({
            username: "apikey",
            password: kcToken
        });
    } else {
        window?.app?.Duchess.loginv3({
            username: "apikey",
            password: kcToken
        });
    }
}

export default () => function* rootSaga() {
    yield takeLatest(LOGOUT_SUCCEEDED, redirectToLogIn);
    yield takeEvery(LOGIN_SUCCEEDED, persistToken);
    yield takeEvery(LOGIN_SUCCEEDED, refreshSessionOrLogIn);
    yield takeLatest(LOGIN_SUCCEEDED, fnInviteUser);
    yield takeEvery(LOGOUT_REQUESTED, cleanupToken);
    yield all([ loginStateSaga(), refreshSession() ]);
};
